import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FlaskConical, Plus, Trophy, X } from 'lucide-react';
import { Sparkle, ArrowsClockwise } from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const C = { bg: '#080808', card: '#141414', border: 'rgba(255,255,255,0.07)', text: '#f5f0e8', dim: 'rgba(245,240,232,0.4)', accent: '#c8f060' };

export default function ABTestingPage() {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: '', content_type: 'flyer', hypothesis: '',
    variant_a: { label: 'Variant A', headline: '', description: '' },
    variant_b: { label: 'Variant B', headline: '', description: '' },
  });

  const token = localStorage.getItem('bf_access_token') || localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchTests = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/ab-test/list`, { headers });
      setTests(data.tests || []);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTests(); }, [fetchTests]);

  const createTest = async () => {
    if (!form.name.trim()) { toast.error('Give your test a name'); return; }
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/api/ab-test/create`, form, { headers });
      toast.success(`A/B Test created! (${data.credits_used} credits)`);
      setShowCreate(false);
      setForm({ name: '', content_type: 'flyer', hypothesis: '', variant_a: { label: 'Variant A', headline: '', description: '' }, variant_b: { label: 'Variant B', headline: '', description: '' } });
      fetchTests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create test');
    } finally { setCreating(false); }
  };

  const vote = async (testId, variant) => {
    try {
      await axios.post(`${API}/api/ab-test/${testId}/vote`, { variant }, { headers });
      toast.success(`Voted for Variant ${variant}`);
      if (selectedTest?.test_id === testId) loadTestDetail(testId);
      fetchTests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Vote failed');
    }
  };

  const closeTest = async (testId) => {
    try {
      await axios.post(`${API}/api/ab-test/${testId}/close`, {}, { headers });
      toast.success('Test closed');
      setSelectedTest(null);
      fetchTests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to close test');
    }
  };

  const loadTestDetail = async (testId) => {
    try {
      const { data } = await axios.get(`${API}/api/ab-test/${testId}`, { headers });
      setSelectedTest(data);
    } catch {}
  };

  return (
    <DashboardLayout>
      <div data-testid="ab-testing-page" className="space-y-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(168,139,250,0.06)', border: `1px solid ${C.border}` }}>
              <FlaskConical size={20} style={{ color: '#a78bfa' }} />
            </div>
            <div>
              <h1 className="text-2xl font-medium font-['Instrument_Serif',serif]" style={{ color: C.text }}>A/B Testing</h1>
              <p className="text-sm" style={{ color: C.dim }}>Compare content variants and find what performs best</p>
            </div>
          </div>
          <button onClick={() => setShowCreate(true)} data-testid="create-ab-test-btn"
            className="flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium" style={{ background: C.accent, color: '#080808' }}>
            <Plus size={16} /> New Test (5 credits)
          </button>
        </motion.div>

        {/* Tests Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" /></div>
        ) : tests.length === 0 ? (
          <div className="text-center py-20 rounded-xl" style={{ background: C.card, border: `1px solid ${C.border}` }}>
            <FlaskConical size={40} className="mx-auto mb-4" style={{ color: C.dim }} />
            <p className="text-lg font-medium mb-2" style={{ color: C.text }}>No tests yet</p>
            <p className="text-sm mb-6" style={{ color: C.dim }}>Create your first A/B test to compare content variants</p>
            <button onClick={() => setShowCreate(true)} className="px-6 py-2.5 rounded-full text-sm font-medium" style={{ background: C.accent, color: '#080808' }}>
              Create First Test
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {tests.map(test => {
              const aVotes = test.variant_a?.votes || 0;
              const bVotes = test.variant_b?.votes || 0;
              const total = aVotes + bVotes;
              const aPct = total > 0 ? Math.round((aVotes / total) * 100) : 50;
              const bPct = total > 0 ? Math.round((bVotes / total) * 100) : 50;
              const isActive = test.status === 'active';

              return (
                <motion.div key={test.test_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  className="rounded-xl p-5 cursor-pointer hover:border-white/20 transition-all"
                  style={{ background: C.card, border: `1px solid ${C.border}` }}
                  onClick={() => loadTestDetail(test.test_id)}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium" style={{ color: C.text }}>{test.name}</h3>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-medium" style={{
                      background: isActive ? 'rgba(200,240,96,0.08)' : 'rgba(168,139,250,0.08)',
                      color: isActive ? '#c8f060' : '#a78bfa',
                      border: `1px solid ${isActive ? 'rgba(200,240,96,0.2)' : 'rgba(168,139,250,0.2)'}`,
                    }}>
                      {isActive ? 'Active' : 'Completed'}
                    </span>
                  </div>
                  {test.hypothesis && <p className="text-[11px] mb-3" style={{ color: C.dim }}>{test.hypothesis}</p>}
                  {/* Comparison bar */}
                  <div className="flex h-3 rounded-full overflow-hidden mb-2" style={{ background: 'rgba(255,255,255,0.04)' }}>
                    <div className="h-full transition-all" style={{ width: `${aPct}%`, background: '#c8f060' }} />
                    <div className="h-full transition-all" style={{ width: `${bPct}%`, background: '#a78bfa' }} />
                  </div>
                  <div className="flex items-center justify-between text-[10px]">
                    <span style={{ color: '#c8f060' }}>A: {aPct}% ({aVotes})</span>
                    <span style={{ color: C.dim }}>{total} responses</span>
                    <span style={{ color: '#a78bfa' }}>B: {bPct}% ({bVotes})</span>
                  </div>
                  {/* Vote buttons */}
                  {isActive && (
                    <div className="flex gap-2 mt-3">
                      <button onClick={e => { e.stopPropagation(); vote(test.test_id, 'A'); }}
                        className="flex-1 py-1.5 rounded-lg text-[11px] font-medium transition-all hover:opacity-80"
                        style={{ background: 'rgba(200,240,96,0.08)', color: '#c8f060', border: '1px solid rgba(200,240,96,0.15)' }}>
                        Vote A
                      </button>
                      <button onClick={e => { e.stopPropagation(); vote(test.test_id, 'B'); }}
                        className="flex-1 py-1.5 rounded-lg text-[11px] font-medium transition-all hover:opacity-80"
                        style={{ background: 'rgba(168,139,250,0.08)', color: '#a78bfa', border: '1px solid rgba(168,139,250,0.15)' }}>
                        Vote B
                      </button>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Detail Modal */}
        <AnimatePresence>
          {selectedTest && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[200] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
              onClick={() => setSelectedTest(null)}>
              <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} className="w-full max-w-lg mx-4 rounded-2xl p-6 max-h-[80vh] overflow-y-auto"
                style={{ background: C.card, border: `1px solid ${C.border}` }} onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-medium" style={{ color: C.text }}>{selectedTest.name}</h2>
                  <button onClick={() => setSelectedTest(null)} className="text-zinc-500 hover:text-white"><X size={18} /></button>
                </div>
                {selectedTest.analysis && (
                  <div className="p-4 rounded-xl mb-4" style={{ background: 'rgba(200,240,96,0.03)', border: '1px solid rgba(200,240,96,0.08)' }}>
                    {selectedTest.analysis.winner ? (
                      <div className="flex items-center gap-2">
                        <Trophy size={18} style={{ color: '#f59e0b' }} />
                        <span className="text-sm font-medium" style={{ color: C.text }}>
                          Variant {selectedTest.analysis.winner} leads with {selectedTest.analysis.confidence}% confidence
                        </span>
                      </div>
                    ) : (
                      <p className="text-sm" style={{ color: C.dim }}>
                        {selectedTest.analysis.total_responses < 10 ? `Need ${10 - selectedTest.analysis.total_responses} more responses for a winner` : 'Tied — no clear winner yet'}
                      </p>
                    )}
                  </div>
                )}
                {/* Variants */}
                {['a', 'b'].map(v => {
                  const variant = selectedTest[`variant_${v}`];
                  const color = v === 'a' ? '#c8f060' : '#a78bfa';
                  const pct = selectedTest.analysis?.[`${v}_percentage`] || 0;
                  return (
                    <div key={v} className="p-4 rounded-xl mb-3" style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${C.border}` }}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-bold" style={{ color }}>{variant?.label || `Variant ${v.toUpperCase()}`}</span>
                        <span className="text-xs font-bold" style={{ color }}>{pct}% ({variant?.votes || 0} votes)</span>
                      </div>
                      {variant?.headline && <p className="text-sm font-medium mb-1" style={{ color: C.text }}>{variant.headline}</p>}
                      {variant?.description && <p className="text-[11px]" style={{ color: C.dim }}>{variant.description}</p>}
                    </div>
                  );
                })}
                {selectedTest.status === 'active' && (
                  <button onClick={() => closeTest(selectedTest.test_id)}
                    className="w-full mt-2 py-2 rounded-lg text-xs font-medium"
                    style={{ border: `1px solid rgba(239,68,68,0.2)`, color: '#ef4444' }}>
                    Close Test
                  </button>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Create Modal */}
        <AnimatePresence>
          {showCreate && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[200] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
              onClick={() => setShowCreate(false)}>
              <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} className="w-full max-w-lg mx-4 rounded-2xl p-6 max-h-[80vh] overflow-y-auto"
                style={{ background: C.card, border: `1px solid ${C.border}` }} onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-lg font-medium" style={{ color: C.text }}>New A/B Test</h2>
                  <button onClick={() => setShowCreate(false)} className="text-zinc-500 hover:text-white"><X size={18} /></button>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="text-[10px] font-semibold uppercase tracking-wider mb-1.5 block" style={{ color: C.dim }}>Test Name</label>
                    <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Hero headline comparison"
                      className="w-full px-3 py-2 rounded-lg text-sm" style={{ background: '#0a0a0a', border: `1px solid ${C.border}`, color: C.text }} />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold uppercase tracking-wider mb-1.5 block" style={{ color: C.dim }}>Content Type</label>
                    <div className="flex gap-2">
                      {['flyer', 'carousel', 'video_ad', 'landing_page'].map(t => (
                        <button key={t} onClick={() => setForm(f => ({ ...f, content_type: t }))}
                          className="px-3 py-1.5 rounded-lg text-[11px] font-medium capitalize"
                          style={{
                            background: form.content_type === t ? 'rgba(200,240,96,0.08)' : 'rgba(255,255,255,0.02)',
                            border: `1px solid ${form.content_type === t ? 'rgba(200,240,96,0.2)' : C.border}`,
                            color: form.content_type === t ? C.accent : C.dim,
                          }}>
                          {t.replace('_', ' ')}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold uppercase tracking-wider mb-1.5 block" style={{ color: C.dim }}>Hypothesis (optional)</label>
                    <textarea value={form.hypothesis} onChange={e => setForm(f => ({ ...f, hypothesis: e.target.value }))} rows={2}
                      placeholder="e.g. A shorter headline will get more clicks"
                      className="w-full px-3 py-2 rounded-lg text-sm resize-none" style={{ background: '#0a0a0a', border: `1px solid ${C.border}`, color: C.text }} />
                  </div>
                  {/* Variant A & B */}
                  {['a', 'b'].map(v => {
                    const color = v === 'a' ? '#c8f060' : '#a78bfa';
                    const variant = form[`variant_${v}`];
                    return (
                      <div key={v} className="p-3 rounded-xl" style={{ border: `1px solid ${color}20` }}>
                        <label className="text-[10px] font-bold uppercase tracking-wider mb-2 block" style={{ color }}>Variant {v.toUpperCase()}</label>
                        <input value={variant.headline} onChange={e => setForm(f => ({ ...f, [`variant_${v}`]: { ...f[`variant_${v}`], headline: e.target.value } }))}
                          placeholder="Headline" className="w-full px-3 py-2 rounded-lg text-sm mb-2"
                          style={{ background: '#0a0a0a', border: `1px solid ${C.border}`, color: C.text }} />
                        <textarea value={variant.description} onChange={e => setForm(f => ({ ...f, [`variant_${v}`]: { ...f[`variant_${v}`], description: e.target.value } }))}
                          placeholder="Description / details" rows={2} className="w-full px-3 py-2 rounded-lg text-sm resize-none"
                          style={{ background: '#0a0a0a', border: `1px solid ${C.border}`, color: C.text }} />
                      </div>
                    );
                  })}
                  <button onClick={createTest} disabled={creating}
                    className="w-full py-3 rounded-full text-sm font-bold flex items-center justify-center gap-2" style={{ background: C.accent, color: '#080808' }}>
                    {creating ? <><ArrowsClockwise size={16} className="animate-spin" /> Creating...</> : <><Sparkle size={16} weight="fill" /> Create A/B Test (5 credits)</>}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </DashboardLayout>
  );
}
